// import styles from './Tab.module.scss';
// import cn from 'classnames';
import { useMemo, useCallback, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from "i18next";
import { formatNumber } from "@utils/formatValues";
import { Filters, FilterTypes } from '@containers/FiltersNew';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import DataTable from '@containers/DataTableNew';
import {
  getFBPProblematicRequests,
  useFBPProblematicRequestsCounter,
  TRestoreRequestsPageItem,
  TRequestParams,
} from "@api/apiClient/fbp";
import { Alert, AlertType } from "@components/new/Alert";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import { TableColumn } from "@components/new/Table";
import OrderModal from "../modals/OrderModal";
import { weightGValue, formatDate } from "@utils/formatValues";
import StatusBadge from "../components/StatusBadge";
import { useKnowledgeBase } from '@contexts/KnowledgeBaseContext';
import InfoIcon from '@assets/icons/outline/info.svg?react';
import styles from './Tab.module.scss';

const ProblematicTab = ({ sellerWarehouseId, warehouseAddress, warehousePhone }: {
  sellerWarehouseId: string;
  warehouseAddress: string;
  warehousePhone: string;
}): ReactElement => {
  const { t } = useTranslation();
  const currentLanguage = i18n.language;
  const [isOpenOrderModal, setOpenOrderModal] = useState<boolean>(false);
  const [currentRequestId, setCurrentRequestId] = useState<string>("");
  const [sorting, setSorting] = useState<{ column: string, direction: string } | undefined>(undefined);
  const { data: problematicCounter } = useFBPProblematicRequestsCounter(sellerWarehouseId ?? "");
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const { setKnowledgeBaseParams } = useKnowledgeBase();

  const columns: TableColumn<TRestoreRequestsPageItem>[] = useMemo(() => [
    {
      header: t("fbpPage.ordersTab.columns.number"),
      valueGetter: (data: TRestoreRequestsPageItem) => data.number,
      width: "150px",
      // fixWidth: true,
    },
    {
      id: "count",
      header: t("fbpPage.ordersTab.columns.count"),
      valueGetter: (data: TRestoreRequestsPageItem) => (data.ymProductsCount + data.wbProductsCount + data.aliProductsCount),
      // alignment: "center",
      width: "160px",
      fixWidth: true,
      // sortable: true,
    },
    {
      id: "weight",
      header: t("fbpPage.ordersTab.columns.weight"),
      valueGetter: (data: TRestoreRequestsPageItem) => {
        const value = (weightGValue(data.wbTotalWeight) + weightGValue(data.ymTotalWeight) + weightGValue(data.aliTotalWeight)) / 1000;
        return value ? formatNumber(value) : "0";
      },
      // alignment: "center",
      width: "90px",
      fixWidth: true,
      // sortable: true,
    },
    {
      id: "createdAt",
      header: t("fbpPage.ordersTab.columns.createdAt"),
      valueGetter: (data: TRestoreRequestsPageItem) => formatDate(data.createdAt, 'dd.MM.yy'),
      // alignment: "center",
      width: "150px",
      fixWidth: true,
      // sortable: true,
    },
    {
      header: t("fbpPage.ordersTab.columns.status"),
      renderCell: (row) => (<StatusBadge value={row.data.status} />),
      // alignment: "center",
      width: "150px",
      fixWidth: true,
    },
  ], [currentLanguage]);

  const ordersGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => getFBPProblematicRequests({ filters, sorting, page, sellerWarehouseId }),
    [sellerWarehouseId]
  );

  return (
    <div className={styles.tabContent}>
      <div className={styles.actionsRow}>
        <Filters
          id="fbp-problematic"
          // openFilters={() => setIsFilterOpen(true)}
          items={[
            {
              name: 'numbers',
              type: FilterTypes.MULTI_SEARCH_NEW,
              placeholder: t('fbpPage.requestSearch'),
            },
          ]}
          onChange={(v) => {
            setInnerFilters(v);
          }}
          values={innerFilters}
          // asPanel
        />

        <div className={styles.actionsRight}>
          <LinkButton
            icon={InfoIcon}
            size={LinkButtonSize.L}
            title={t('fbpPage.instructions')}
            onClick={() => setKnowledgeBaseParams(`https://unitrade-global.com/page139704916.html`)}
          />
        </div>
      </div>
      <Alert
        type={AlertType.WARNING}
        title={t("fbpPage.problematicAlert", { address: warehouseAddress, phone: warehousePhone })}
        isWide
      />
      <DataTable<TRestoreRequestsPageItem>
        key={sellerWarehouseId}
        columns={columns}
        filters={innerFilters}
        queryKey={["getFBPProblematicRequests", sellerWarehouseId]}
        getData={ordersGetter}
        rowOnClick={(row) => {
          setCurrentRequestId(row.id);
          setOpenOrderModal(true);
        }}
        verticalAlignment="top"
        noLoaderOnRefresh
        noDataProps={problematicCounter ? undefined : {
          title: t("fbpPage.noProblematicRequests"),
          width: "max-content",
        }}
        pending={!sellerWarehouseId}
        sorting={sorting}
        onSortingChange={(column, direction) => setSorting({ column, direction })}
      />
      {isOpenOrderModal ? (
        <OrderModal
          onClose={() => setOpenOrderModal(false)}
          sellerWarehouseId={sellerWarehouseId}
          id={currentRequestId}
          warehouseAddress={warehouseAddress}
          warehousePhone={warehousePhone}
        />
      ) : null}
    </div>
  );
};

export default ProblematicTab;
